import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import * as ButtonGroup from '@givebutter/primitives/button-group';
import { Label } from '@givebutter/primitives/label';
import { maskDecimal, maskWholeNumber } from '@givebutter/util';
import {
    faEdit,
    faInfinity,
    faPlus,
    faRectangleHistory,
    faTicket,
    faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchCampaignItems } from 'api/campaigns';
import * as DeleteItemAlert from 'assets/js/pages/campaign-settings/campaign-event/DeleteItemAlert';
import * as DropdownMenu from 'components/dropdown-menu';
import { transformSavedFilterGroups } from 'components/filter/filters-next/downstream';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import * as Tooltip from 'components/tooltip';
import { EmptyData } from 'components/util';
import { useFiltersCache } from 'contexts/useFiltersCache';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { CampaignItem } from 'types/domain';
import { OutboundFilters } from 'types/request';

const columnHelper = createColumnHelper<CampaignItem>();

const EventTickets = () => {
    const history = useHistory();
    const currency = useCurrencySymbol();
    const { accountId } = useCurrentAccount();
    const updateFilterByKey = useFiltersCache(state => state.updateFiltersNextByKey);
    const { data: campaign } = useCurrentCampaign();
    const [search, setSearch] = usePersistentSearch();
    const searchQuery = useDebounce(search);
    const [campaignItemsIndexTab, setCampaignItemsIndexTab] = useState('tickets');
    const ticketIcon = faTicket;
    const emptyText =
        'Add tickets for your supporters to purchase. Once you have tickets, you can add them to bundles.';

    const { expanded, pagination, setPagination, setExpanded, sorting, setSorting } =
        Table.useReactTableState({
            sortingState: [{ id: 'order', desc: false }],
        });

    const params = {
        query: searchQuery || undefined,
        page: pagination.pageIndex + 1,
        is_auction: false,
        ...Table.sortQuery(sorting),
    };

    const [isEventTicketsPending, eventTicketParams] = useIsPending(
        {
            is_bundle: false,
            include: 'bundles',
            ...params,
        },
        500,
    );

    const [isEventBundlesPending, eventBundleParams] = useIsPending(
        {
            is_bundle: true,
            ...params,
        },
        500,
    );

    const { data: eventTicketsUnfiltered } = useSuspenseQuery({
        queryKey: campaignQueryKeys.itemsFiltered(campaign.id, {
            is_auction: false,
            is_bundle: false,
        }),
        queryFn: () =>
            fetchCampaignItems({
                campaignId: campaign.id,
                is_auction: false,
                is_bundle: false,
            }),
    });

    const { data: eventTickets } = useSuspenseQuery({
        queryKey: campaignQueryKeys.itemsFiltered(campaign.id, eventTicketParams),
        queryFn: () => fetchCampaignItems({ campaignId: campaign.id, ...eventTicketParams }),
    });

    const { data: eventBundles } = useSuspenseQuery({
        queryKey: campaignQueryKeys.itemsFiltered(campaign.id, eventBundleParams),
        queryFn: () => fetchCampaignItems({ campaignId: campaign.id, ...eventBundleParams }),
    });

    const items = campaignItemsIndexTab === 'tickets' ? eventTickets : eventBundles;

    const columns = useMemo(
        () => [
            columnHelper.accessor('name', {
                header: 'Name',
                cell: ({ row, getValue }) => {
                    const { bundle_only: bundleOnly, scope } = row.original;

                    return (
                        <div className="flex gap-2">
                            <p className={'line-clamp-1 font-medium'}>{getValue()}</p>
                            {bundleOnly && (
                                <Tooltip.Root>
                                    <Tooltip.Trigger>
                                        <Badge size="sm" color="primary">
                                            Bundle only
                                        </Badge>
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                        <Tooltip.Content inverse>
                                            <Tooltip.Arrow inverse />
                                            <p>
                                                This ticket can only be purchased as a part of a
                                                bundle.
                                            </p>
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                </Tooltip.Root>
                            )}
                            {scope === 'registrant' && (
                                <Tooltip.Root>
                                    <Tooltip.Trigger>
                                        <Badge size="sm" color="primary">
                                            Fundraisers
                                        </Badge>
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                        <Tooltip.Content inverse>
                                            <Tooltip.Arrow inverse />
                                            <p>This ticket is for registration fundraisers only.</p>
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                </Tooltip.Root>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('sold', {
                header: 'QTY sold',
                enableSorting: false,
                cell: ({ getValue }) => <p>{maskWholeNumber(getValue())}</p>,
            }),
            columnHelper.display({
                id: 'remaining',
                header: 'QTY remaining',
                cell: ({ row }) => {
                    const { total_quantity, bundle_only, remaining } = row.original;

                    const formattedRemaining = total_quantity ? (
                        maskWholeNumber(remaining)
                    ) : (
                        <FontAwesomeIcon icon={faInfinity} />
                    );

                    const display = bundle_only ? 'N/A' : formattedRemaining;
                    return <p className={'whitespace-nowrap'}>{display}</p>;
                },
            }),
            columnHelper.accessor('bundle_quantity', {
                header: 'Bundle QTY',
                cell: ({ row }) => {
                    const { bundle_quantity, bundle_only, has_unlimited_bundle_quantity } =
                        row.original;

                    if (has_unlimited_bundle_quantity) {
                        return <FontAwesomeIcon icon={faInfinity} />;
                    }

                    const bundleQuantity = bundle_quantity || 0;

                    if (!bundle_only && !bundleQuantity) {
                        return <EmptyData />;
                    }

                    return (
                        <p className={'line-clamp-1 font-medium'}>
                            {maskWholeNumber(bundleQuantity)}
                        </p>
                    );
                },
            }),
            columnHelper.accessor('price', {
                header: 'Price',
                cell: ({ getValue }) => {
                    const value = getValue();
                    if (!value) {
                        return <p>Free</p>;
                    }

                    return (
                        <p>
                            {currency}
                            {maskDecimal(value)}
                        </p>
                    );
                },
            }),
            columnHelper.accessor('item_status', {
                header: 'Status',
                enableSorting: false,
                cell: info => {
                    const {
                        item_status: itemStatus,
                        is_bundle_sold_out,
                        is_bundle_item,
                    } = info.row.original;

                    if (itemStatus === 'disabled') {
                        return <Badge size="sm">Unavailable</Badge>;
                    }

                    if (itemStatus === 'sold_out') {
                        if (is_bundle_item) {
                            if (is_bundle_sold_out) {
                                return <SoldOutBadge />;
                            } else {
                                return <LiveBadge />;
                            }
                        } else {
                            return <SoldOutBadge />;
                        }
                    }

                    if (itemStatus === 'available') {
                        return <LiveBadge />;
                    }

                    return <EmptyData />;
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: ({ row }) => (
                    <DropdownMenu.Root>
                        <Table.TableRowDropdownTrigger />
                        <DropdownMenu.Portal>
                            <DropdownMenu.Content sideOffset={4} align={'end'}>
                                <DropdownMenu.LinkItem
                                    to={
                                        campaignItemsIndexTab === 'tickets'
                                            ? `/accounts/${accountId}/campaigns/${campaign.id}/event/tickets/${row.original.id}/edit`
                                            : `/accounts/${accountId}/campaigns/${campaign.id}/event/tickets/bundles/${row.original.id}/edit`
                                    }
                                >
                                    <FontAwesomeIcon icon={faEdit} className={'mr-2'} />
                                    Edit
                                </DropdownMenu.LinkItem>

                                <DropdownMenu.Item
                                    className={'text-red-600 hover:!text-red-600'}
                                    onSelect={e => e.preventDefault()}
                                >
                                    <DeleteItemAlert.Root item={row.original}>
                                        <DeleteItemAlert.Trigger asChild>
                                            <p>
                                                <FontAwesomeIcon
                                                    icon={faTrash}
                                                    className={'mr-2 text-red-700'}
                                                />
                                                Delete
                                            </p>
                                        </DeleteItemAlert.Trigger>
                                    </DeleteItemAlert.Root>
                                </DropdownMenu.Item>
                            </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                ),
                meta: {
                    ignoreLink: true,
                },
            }),
        ],
        [accountId, campaign.id, currency, campaignItemsIndexTab],
    );

    const table = useReactTable({
        data: items.data ?? [],
        columns,
        pageCount: items?.meta?.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
            columnVisibility: {
                bundle_quantity: campaignItemsIndexTab === 'tickets',
            },
            expanded,
            sorting,
        },
        onPaginationChange: setPagination,
        onExpandedChange: setExpanded,
        onSortingChange: setSorting,
        manualPagination: true,
        manualSorting: true,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            to: cell =>
                campaignItemsIndexTab === 'tickets'
                    ? `/accounts/${accountId}/campaigns/${campaign.id}/event/tickets/${cell.row.original.id}/edit`
                    : `/accounts/${accountId}/campaigns/${campaign.id}/event/tickets/bundles/${cell.row.original.id}/edit`,
        },
    });

    useSyncPaginatedData(table, items);

    const hasTickets = eventTicketsUnfiltered?.data.length;

    const handleViewPurchasesClick = () => {
        const values: OutboundFilters = [
            {
                type: 'or',
                children: {
                    campaigns: [
                        {
                            type: 'and',
                            value: [campaign.id.toString()],
                            operator: '=',
                        },
                    ],
                },
            },
        ];

        updateFilterByKey('tickets', transformSavedFilterGroups(values));
        history.push(`/accounts/${accountId}/tickets`);
    };

    return (
        <div className={'flex flex-col gap-y-2'}>
            <div className={'flex items-center justify-between gap-4'}>
                <div className={'flex items-center gap-x-4'}>
                    <h1 className={'self-baseline text-2xl font-medium leading-snug'}>Tickets</h1>
                </div>

                <div className={'flex items-center gap-x-2'}>
                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                            <Button color={'neutral'} variant={'outline'}>
                                Actions
                                <FontAwesomeIcon icon={faChevronDown} />
                            </Button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                            <DropdownMenu.Content sideOffset={4} align={'end'}>
                                <DropdownMenu.Item
                                    disabled={!hasTickets}
                                    onClick={() => {
                                        history.push(
                                            `/accounts/${accountId}/campaigns/${campaign.id}/event/tickets/organize`,
                                        );
                                    }}
                                >
                                    Organize
                                </DropdownMenu.Item>
                                {campaignItemsIndexTab === 'tickets' && (
                                    <DropdownMenu.Item
                                        disabled={!hasTickets}
                                        onClick={() => handleViewPurchasesClick()}
                                    >
                                        View purchases
                                    </DropdownMenu.Item>
                                )}
                            </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                    </DropdownMenu.Root>

                    <Button
                        variant="solid"
                        onClick={() => {
                            history.push(
                                campaignItemsIndexTab === 'tickets'
                                    ? `/accounts/${accountId}/campaigns/${campaign.id}/event/tickets/new`
                                    : `/accounts/${accountId}/campaigns/${campaign.id}/event/tickets/bundles/new`,
                            );
                        }}
                        disabled={campaignItemsIndexTab === 'bundles' && !hasTickets}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        New {campaignItemsIndexTab === 'tickets' ? 'ticket' : 'bundle'}
                    </Button>
                </div>
            </div>
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-2">
                    <ButtonGroup.Root
                        type="single"
                        value={campaignItemsIndexTab}
                        onValueChange={value => setCampaignItemsIndexTab(value)}
                    >
                        <ButtonGroup.Item value={'tickets'}>Tickets</ButtonGroup.Item>
                        <ButtonGroup.Item value={'bundles'} disabled={!eventTickets.data.length}>
                            Bundles
                        </ButtonGroup.Item>
                    </ButtonGroup.Root>
                    <div>
                        <Label className={'sr-only'} htmlFor={'table-search'}>
                            Search {campaignItemsIndexTab}
                        </Label>
                        <Input.Root size={'sm'}>
                            <Input.SearchIcon />
                            <Input.Form
                                id={'table-search'}
                                value={search}
                                placeholder={'Search'}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </Input.Root>
                    </div>
                </div>

                <div className={'flex flex-col gap-y-8 overflow-x-auto'}>
                    <Table.Root
                        isLoading={isEventBundlesPending || isEventTicketsPending}
                        table={table}
                        variant={'bordered'}
                        searchTerm={searchQuery}
                    >
                        <Box className={'overflow-x-auto'}>
                            <Table.Content>
                                <Table.Header />
                                <Table.Body />
                            </Table.Content>
                            <Table.Empty variant={'primary'}>
                                <div className="flex flex-col items-center gap-6">
                                    <Table.EmptyIcon>
                                        <FontAwesomeIcon
                                            icon={
                                                campaignItemsIndexTab === 'tickets'
                                                    ? ticketIcon
                                                    : faRectangleHistory
                                            }
                                        />
                                    </Table.EmptyIcon>
                                    <div className="flex flex-col gap-1">
                                        <Table.EmptyTitle>
                                            No {campaignItemsIndexTab} found
                                        </Table.EmptyTitle>
                                        <Table.EmptyDescription className="min-w-[350px]">
                                            {emptyText}
                                        </Table.EmptyDescription>
                                    </div>

                                    <Button
                                        variant="solid"
                                        onClick={() => {
                                            history.push(
                                                campaignItemsIndexTab === 'tickets'
                                                    ? `/accounts/${accountId}/campaigns/${campaign.id}/event/tickets/new`
                                                    : `/accounts/${accountId}/campaigns/${campaign.id}/event/tickets/bundles/new`,
                                            );
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                        New{' '}
                                        {campaignItemsIndexTab === 'tickets' ? 'ticket' : 'bundle'}
                                    </Button>
                                </div>
                            </Table.Empty>
                        </Box>
                        <Table.Pagination>
                            <Table.PaginationPageNavigation />
                        </Table.Pagination>
                    </Table.Root>
                </div>
            </div>
        </div>
    );
};

const SoldOutBadge = () => (
    <Badge size="sm" color="error">
        Sold out
    </Badge>
);

const LiveBadge = () => (
    <Badge size="sm" color="success">
        Live
    </Badge>
);

export { EventTickets };
